/**
 * ACHIEVEMENTS — progress-based goals that grant a BADGE plus optional rewards (coins, petals, xp, seeds,
 * decor items) when their conditions are met.
 *
 * POT SKINS COME FROM TWO PLACES. The five first-generation skins (midnight, porcelain, mossy, urn, pedestal)
 * are paid by named rungs of the ACHIEVEMENT POINT TRACK (`reward.potSkin`, derived by `skinsFromMilestones` —
 * sim/pointTrack.ts). Every reward pot — those five again plus the second generation — is ALSO paid by exactly
 * one hand-written achievement below, through `reward.unlocks` ("pot:<id>"), alongside the shelf, floor and
 * building skins; see THE SKINS ARE DEALT BY PRESTIGE above DEFAULT_ACHIEVEMENTS. The older per-achievement
 * `potSkin` field is still read (`skinsFromAchievements`, `cosmeticsFromAchievements`), so a live tuning blob
 * that carries one keeps paying it; the source defaults use `unlocks` instead.
 *
 * Design: pure + data-driven, and now DB-CONFIGURABLE. `ACHIEVEMENTS` (source defaults) is the fallback;
 * the live `achievements` tuning key is merged over it at boot, so the whole ladder — names, conditions,
 * thresholds, rewards, badges — can be retuned from the in-game dev menu WITHOUT A DEPLOY, exactly like
 * `checkinRewards`, `fishConditions` and `decorPrices` already are. A git push can never clobber a live
 * edit, because the live values are rows in Postgres, not lines in this file.
 *
 * `evaluateAchievements(stats)` stays a pure function returning the unlocked id set for a stats snapshot —
 * no side effects, trivially testable, and the caller diffs it against what was already unlocked to fire
 * the "newly unlocked" celebrations.
 *
 * CONDITIONS. An achievement holds a LIST of conditions, all of which must hold (AND). One condition is
 * still just `metric ≥ threshold`, so the original single-metric achievements are the one-element case and
 * old saved configs keep working. The legacy top-level `metric` + `threshold` fields are still READ (see
 * `conditionsOf`) so a tuning blob written before this change still evaluates correctly.
 *
 * REWARDS are split into two kinds, and the distinction matters:
 *   • DERIVED (potSkin, unlocks) — recomputed from the unlocked set every render. Idempotent; nothing to
 *     persist. The source defaults pay their skins through `unlocks`; `potSkin` is the older one-kind form
 *     (see POT SKINS COME FROM TWO PLACES above).
 *   • GRANTED (coins, petals, xp, fishXp, bugXp, seeds, decor) — applied ONCE, at the moment of unlock, by the
 *     caller. These must never be re-applied on load, or every reload would pay the player again. App.tsx
 *     only grants for ids that are freshly added to the unlocked set, which is the same guard the daily
 *     check-in uses (see checkin.ts `claimCheckin`).
 *
 * FINDING ONE ACHIEVEMENT AMONG 1,297 — 52 hand-written below, plus 1,245 generated (545 harvest + 350 fish +
 * 350 insect); no id collides, so the live ladder is the sum. The generated ladders
 * (sim/achievementFamilies.ts) mean the list is far too long to scroll, so this module carries the two axes
 * a player-facing list needs and the UI does not get to invent:
 *   • CATEGORY (`categoryOf`) — plants / fish / insects / garden. The filter axis. Optional on the def and
 *     INFERRED from the achievement's metrics when unset, so nothing ever falls out of a filtered list.
 *   • FAMILY (`familyOf`) — the other rungs of the same species ladder, for a related-achievements panel.
 *     Grouped on the parameterized metric the achievement already carries; the ids are not parsed and not changed.
 */

import { FLOWERS, ALL_PLANT_IDS, type FlowerId } from "./flowers";
import { BACKER_COSMETIC_KEYS } from "./backerCosmetics";
import { resolveBadge, type BadgeRef, type ResolvedBadge } from "./badges";
import { MAX_PLOT_CELLS } from "./garden";
import { MAX_RACKS } from "./gadgets";
import { CHECKIN_DAYS } from "./checkin";
import { PREMIUM_PLANT_IDS } from "./level";
import { FISH } from "./fishing";
import { INSECTS } from "./insects";
import { grantedPetals } from "./subscription";
import { isOut } from "./releases";

/** The live stat snapshot achievements read from. Assembled by the caller from the save + derived values
 *  (e.g. gardening level from xp). Keep every field a plain non-negative number. */
export interface AchievementStats {
  harvestCount: number;     // total plants harvested (greenhouse + garden)
  gardenLevel: number;      // current Gardening level (derived from xp)
  fishingLevel: number;     // current Fishing level (derived from fishXp)
  bugLevel: number;         // current Naturalist (bug) level (derived from bugXp)
  fishSpecies: number;      // distinct fish species caught
  fishCount: number;        // total fish caught
  insectSpecies: number;    // distinct insects collected
  insectCount: number;      // total insects collected
  coins: number;            // current coins
  petals: number;           // current petals (premium currency)
  plantSpecies: number;     // distinct plant species ever harvested
  decorPlaced: number;      // decor items currently placed in the garden
  checkinDays: number;      // squares claimed on the daily check-in board
  /** Plot CELLS the garden occupies, INCLUDING the free starting cell — so a brand-new garden reads 1, not
   *  0, and the top milestone's 50 is the same number the expansion UI shows. Counted rather than remembered:
   *  it is `plot.cells.length`, so selling a plot back moves it down again. */
  plotCells: number;
  /** Greenhouse shelves (racks) owned, 1..MAX_RACKS. Also inclusive of the starting shelf. */
  shelfCount: number;
  /** Painted water cells in the garden. This is the FINE sub-grid (WATER_DIV² = 484 cells per plot cell), not
   *  a count of brush strokes — one stroke of the round brush lays down roughly 55 of these, so thresholds
   *  here live in the hundreds and thousands. Compare `pondAreaMin` in sim/fishConds.ts, which is the same
   *  unit: the fish that want "big water" ask for 200–400. */
  waterCells: number;
  /** Composts the scrap composter has made, lifetime. */
  compostsMade: number;
  /** The best compost rank ever made (0 = none yet, 1..5). */
  bestCompostRank: number;
  /** Greenhouse pots holding a compost of ★★★ or better right now. */
  compostPotsThreePlus: number;
  /** PARAMETERIZED metrics, keyed "<family>:<id>" — e.g. "harvested:rose" → 37. Read by any condition whose
   *  metric contains a colon. Keeping these in one map (rather than as interface fields) is what lets the
   *  plant roster grow without touching this type or the evaluator. */
  counters: Record<string, number>;
}

/**
 * The metric a condition reads. Either a fixed key of AchievementStats, or a PARAMETERIZED metric of the
 * form "<family>:<id>" — e.g. "harvested:rose".
 *
 * Parameterized metrics exist because the alternative was 109 more fields on AchievementStats (one per
 * plant), growing every time a species is added. Instead the stats snapshot carries one `counters` map and
 * a metric names a key inside it, so a new plant needs no type change at all.
 */
export type AchievementMetric = keyof AchievementStats | `${string}:${string}`;

/** The fixed metric keys — the ones AchievementStats declares as real fields. */
export type FixedMetric = Exclude<keyof AchievementStats, "counters">;

/** Player-facing labels for the metric dropdown in the dev editor. Parameterized metrics ("harvested:rose")
 *  are not listed here — there are thousands of them and they are named by their generator instead. */
export const METRIC_LABELS: Record<FixedMetric, string> = {
  harvestCount: "Plants harvested (total)",
  gardenLevel: "Gardening level",
  fishingLevel: "Fishing level",
  bugLevel: "Bug Catching level",
  fishSpecies: "Fish species caught",
  fishCount: "Fish caught (total)",
  insectSpecies: "Insect species collected",
  insectCount: "Insects collected (total)",
  coins: "Coins held",
  petals: "Petals held",
  plantSpecies: "Plant species harvested",
  decorPlaced: "Decor items placed",
  checkinDays: "Daily check-ins claimed",
  plotCells: "Garden plots owned",
  shelfCount: "Greenhouse racks owned",
  waterCells: "Water cells painted",
  compostsMade: "Composts made",
  bestCompostRank: "Best compost rank made",
  compostPotsThreePlus: "Pots with ★★★+ compost",
};

export const METRIC_IDS: FixedMetric[] = Object.keys(METRIC_LABELS) as FixedMetric[];

/** A DISPLAY NAME for anything reward or progress text names by id — a skin ("building:palmHouse" → "Palm House"),
 *  a species ("plant:rose" → "Rose"), a decor bag key. Registered by the UI at
 *  start-up (sim/cosmeticNames.ts) — achievements.ts must not import the skin registries (a cycle). */
let unlockNamer: (kind: string, id: string) => string = (_k, id) => id;
export function setUnlockNamer(f: (kind: string, id: string) => string): void { unlockNamer = f; }
function unlockName(kind: string, id: string): string { return unlockNamer(kind, id); }

/** A readable label for ANY metric, including parameterized ones. */
export function metricLabel(m: AchievementMetric): string {
  if (!m.includes(":")) return METRIC_LABELS[m as FixedMetric] ?? m;
  const [family, id] = m.split(":");
  // The generated families read as a phrase; anything else falls back to the raw "family: id".
  // the species' DISPLAY name (registered by the UI — sim/cosmeticNames.ts), never its id
  if (family === "harvested") return `${unlockName("plant", id ?? "")} harvested`;
  if (family === "fishCaught") return `${unlockName("fish", id ?? "")} caught`;
  if (family === "insectCaught") return `${unlockName("insect", id ?? "")} collected`;
  return `${family}: ${id}`;
}

/**
 * WHAT KIND OF THING this achievement is about — the axis a player filters the list by.
 *
 * Four buckets, because four is what the 1,296-achievement ladder actually splits into: the three collection
 * ladders (plants / fish / insects) plus everything that is about the PLACE rather than a species — land,
 * shelves, water, decor, coins, check-ins, and the skill tracks that measure them. Deliberately not one
 * bucket per metric: a filter with sixteen options is a list of its own, and "Coin Purse" and "Landholder"
 * are the same kind of goal from a player's point of view even though they read different numbers.
 *
 * NOT a replacement for the metric. The metric says what is counted; the category says where the achievement
 * belongs in a menu. An achievement can read `harvestCount` and still be `garden` (a landmark about the place)
 * or `plants` (a species ladder rung) — which is why it is stated rather than always inferred.
 */
export type AchievementCategory = "plants" | "fish" | "insects" | "garden";

/** The categories in the order a filter control should offer them: the three collections that make up the
 *  bulk of the ladder, then the catch-all. */
export const ACHIEVEMENT_CATEGORIES: AchievementCategory[] = ["plants", "fish", "insects", "garden"];

/** Player-facing labels for the category filter. */
export const CATEGORY_LABELS: Record<AchievementCategory, string> = {
  plants: "Plants",
  fish: "Fish",
  insects: "Bugs",
  garden: "Garden",
};

/**
 * Where an achievement with NO stated category lands. `garden` is the catch-all on purpose: it is the bucket for
 * "about the place rather than a species", so an achievement that predates this field — or one authored in the
 * dev menu by someone who never saw the dropdown — shows up under the broadest heading rather than
 * vanishing from a filtered list. Never make this one of the species buckets; a mystery achievement appearing
 * among the rose ladder is worse than one appearing among the landmarks.
 */
export const DEFAULT_CATEGORY: AchievementCategory = "garden";

/** Fixed metrics that imply a category on their own. Only the unambiguous ones are listed — `harvestCount`
 *  is absent because "harvest 150 plants" is a landmark about the garden as much as about plants, and
 *  guessing wrong there would scatter the hand-authored ladder across three filters. */
const METRIC_CATEGORY: Partial<Record<FixedMetric, AchievementCategory>> = {
  fishSpecies: "fish",
  fishCount: "fish",
  fishingLevel: "fish",
  insectSpecies: "insects",
  insectCount: "insects",
  bugLevel: "insects",
  plantSpecies: "plants",
};

/** Parameterized metric FAMILIES ("harvested:rose" → plants). Keyed by the part before the colon, so the
 *  three generated ladders classify themselves and a future family only needs a line here. The family
 *  names are owned by sim/achievementFamilies.ts; they are repeated as literals rather than imported
 *  because that module imports AchievementDef from this one and a back-import would be a cycle. */
const FAMILY_CATEGORY: Record<string, AchievementCategory> = {
  harvested: "plants",
  fishCaught: "fish",
  insectCaught: "insects",
};

/**
 * This achievement's category. Stated wins; otherwise it is INFERRED from the metrics the achievement reads, and only
 * then does it fall back to `DEFAULT_CATEGORY`.
 *
 * Inferring rather than defaulting straight to the fallback is what keeps a filtered list honest for achievements
 * written before this field existed: `fishCaught:koi` and `insectSpecies` already say what they are about,
 * and a dev-authored achievement built on one of them lands in the right bucket with nothing filled in. An achievement
 * whose conditions disagree (one fish metric, one plant metric) takes the FIRST condition's answer — a
 * deterministic choice beats a "mixed" bucket nobody thought to put in the filter.
 */
export function categoryOf(a: AchievementDef): AchievementCategory {
  if (a.category && ACHIEVEMENT_CATEGORIES.includes(a.category)) return a.category;
  for (const c of a.conditions ?? []) {
    const m = c.metric;
    const hit = m.includes(":")
      ? FAMILY_CATEGORY[m.slice(0, m.indexOf(":"))]
      : METRIC_CATEGORY[m as FixedMetric];
    if (hit) return hit;
  }
  return DEFAULT_CATEGORY;
}

/** One condition: `metric` compared against `threshold`. `atLeast` is the default and by far the common case;
 *  `atMost` exists for "finish with under N" style goals. */
export interface AchievementCondition {
  metric: AchievementMetric;
  threshold: number;
  cmp?: "atLeast" | "atMost";
}

/** A reward granted when an achievement unlocks. `potSkin` is DERIVED (safe to recompute); everything else
 *  is GRANTED ONCE at the moment of unlock — see the module header. */
export interface AchievementReward {
  potSkin?: string;         // a POT_SKINS id made selectable
  /** COSMETICS this achievement unlocks, as "kind:id" (sim/cosmetics.ts: pot / building / floor / shelf).
   *  DERIVED like `potSkin` — recomputed from the held set, never stored — so it is stripped from the grant. */
  unlocks?: string[];
  /** HEAD GARDENER's reward: a Botanical Marker for each of the player's N most-harvested plants, granted
   *  once when the achievement unlocks (a trophy — see sim/decor.ts isTrophy). */
  topMarkers?: number;
  coins?: number;
  petals?: number;
  xp?: number;              // gardening xp
  fishXp?: number;
  bugXp?: number;
  /** Seed packets: species id → count, added to the seed inventory. */
  seeds?: Partial<Record<FlowerId, number>>;
  /**
   * DECOR ITEMS: `DecorId` → count, added to the garden's OWNED decor bag (`decorOwned`) exactly as a shop
   * purchase does. The player then places them from the decorate panel like anything else they bought.
   *
   * GRANTED ONCE, not derived — deliberately unlike `potSkin`. A skin is a permission (you either may dress
   * a pot that way or you may not, and recomputing it from the unlocked set is always safe); a decor item is
   * an OBJECT with a count, which the player can place, pick up and SELL back for coins. Recomputing that
   * from the unlocked set every render would silently re-stock every item they ever sold. So decor rides
   * with `seeds` in the granted half of the split, and `grantOf` / `milestoneGrant` pass it through.
   *
   * The id is NOT typed as `DecorId` here: rewards are authored in a dev editor and arrive through untrusted
   * tuning JSON, so a retired or mistyped id must be a dead entry the editor can flag, not a type error that
   * cannot be represented. `unknownDecorKeys` (sim/decor.ts) finds them; the grant path skips anything not in the catalogue.
   */
  decor?: Record<string, number>;
}

export interface AchievementDef {
  id: string;
  name: string;
  desc: string;             // how to earn it (player-facing)
  icon: string;             // emoji shown in the list (the BADGE is the real art; this is the compact row glyph)
  /**
   * WHICH FILTER BUCKET this achievement belongs to. OPTIONAL: read it through `categoryOf`, never directly —
   * an unset category is inferred from the achievement's metrics and only then falls back to `DEFAULT_CATEGORY`,
   * so an achievement written before this field existed still lands somewhere sane instead of disappearing from
   * a filtered list.
   */
  category?: AchievementCategory;
  /** ALL of these must hold for the achievement to unlock. */
  conditions?: AchievementCondition[];
  reward?: AchievementReward;
  /** The BADGE: which style to draw and what to put on it. ~20 bytes, versus the ~413 a full badge record
   *  cost — see sim/badges.ts for why that ratio is the whole architecture. */
  badge?: BadgeRef;
  /** Hide from the list until earned (for surprise/secret achievements). A secret achievement still occupies a
   *  REDACTED row in the ladder — the player knows something is there to find. */
  secret?: boolean;
  /**
   * GRANTED, not earned: this achievement comes from outside gameplay (a Kickstarter entitlement, a promo code,
   * a gift). It is hidden completely until granted — not even as a redacted row — because unlike a secret
   * there is no way for a player to discover it, so teasing it would only read as a goal they have failed.
   */
  granted?: boolean;
  /** Set false to disable an achievement without deleting it (it stops unlocking and leaves the list). */
  /** a per-species achievement inherits its species' content release (sim/releases.ts): until that release is out it
   *  is neither listed nor earnable — an unreleased species must not be named by its trophy ladder */
  release?: string;
  enabled?: boolean;
  /**
   * ACHIEVEMENT POINTS, 1..10 — how much this achievement is worth on a player's score.
   *
   * 1 is a first step anyone reaches in minutes; 10 is a genuine long-haul goal. The scale is deliberately
   * short: a 1..10 range keeps every achievement's worth legible at a glance and stops the total inflating into a
   * meaningless number. Missing = `DEFAULT_POINTS`, so an achievement added without a value still scores.
   */
  points?: number;
}

/** Points an achievement is worth when it does not say. Deliberately low: an unscored achievement should not outrank a
 *  deliberately-weighted one. */
export const DEFAULT_POINTS = 1;
/** The scale's bounds. Enforced on read so a bad tuning edit cannot mint an arbitrary score. */
export const MIN_POINTS = 1;
export const MAX_POINTS = 10;

/** An achievement's point value, clamped into the legal range. */
export function pointsOf(a: AchievementDef): number {
  const p = typeof a.points === "number" && Number.isFinite(a.points) ? Math.round(a.points) : DEFAULT_POINTS;
  return Math.max(MIN_POINTS, Math.min(MAX_POINTS, p));
}

/** Total points for a set of unlocked ids. Unknown ids score 0 — an id from a deleted or renamed achievement must
 *  not keep paying out, and must not crash the total either. */
export function totalPoints(unlocked: Iterable<string>): number {
  let sum = 0;
  for (const id of unlocked) {
    const a = BY_ID.get(id);
    if (a) sum += pointsOf(a);
  }
  return sum;
}

/**
 * The maximum score currently obtainable — the denominator for "312 / 2480" on a profile.
 *
 * ACHIEVEMENTS WITH NO CONDITIONS ARE NOT IN THE CEILING unless the player already holds one. `evaluateAchievements`
 * skips a condition-less achievement (see the `!cs.length` guard), so no amount of play can ever unlock one; the
 * backer `founder` badge and any promo code are handed out by the redemption path writing the id straight
 * into `unlockedAchievements`. Summing them into the denominator made the profile advertise a maximum that
 * nobody could reach — "312 / 2480" where the last 10 points were unreachable by playing.
 *
 * They are counted when HELD, so a backer's bar still reads N/N rather than overflowing its own ceiling.
 * Pass the player's unlocked set for a player-facing denominator; omit it (the admin point-track editor) to
 * measure only what the roster mints through play.
 */
export function maxPoints(unlocked?: Iterable<string>): number {
  const held = unlocked instanceof Set ? (unlocked as ReadonlySet<string>) : new Set(unlocked ?? []);
  let sum = 0;
  for (const a of ACHIEVEMENTS) {
    if (a.enabled === false) continue;
    if (!conditionsOf(a).length && !held.has(a.id)) continue;
    sum += pointsOf(a);
  }
  return sum;
}

/**
 * THE SOURCE-DEFAULT ACHIEVEMENTS. Ordered roughly by how early a player reaches them (early → late) so the
 * panel reads as a ladder. These are the FALLBACK — the live `achievements` tuning key overrides them per id.
 *
 * THESE ARE THE LANDMARKS, and they are HAND-AUTHORED for a reason. The per-species ladders in
 * achievementFamilies.ts are generated because there are thousands of them and they must be identical; these
 * are a few dozen one-offs marking the broad things a player does — expanding the garden, filling the
 * greenhouse, digging water, furnishing the place, showing up daily, topping out a skill. Each names its own
 * BADGE_STYLES entry (see the "LANDMARK STYLES" block in sim/badges.ts) rather than a t1..t5 rung, so a case
 * of these reads as a shelf of individual achievements instead of one emblem repeated.
 *
 * THE POINT BUDGET. `pointsOf` clamps to 1..10 and `totalPoints` sums every enabled achievement, so these compete
 * with ~5,700 points of generated ladder. They are weighted against it rather than in isolation:
 *   • 1–2  — a first step, reached in the opening session. Matches a ladder tier-1/2 badge.
 *   • 3–5  — a real session goal: a quarter-garden, three shelves, a fortnight of check-ins.
 *   • 6–8  — a long haul: a filled greenhouse, a hundred decor items, the full check-in board.
 *   • 9–10 — a COMPLETION. Something with nothing above it: all 50 plots, level 50 on a track, every
 *            fish species. Reserved — awarding 10 for a merely-large number is what makes a score stop
 *            meaning anything.
 * That puts the hand-authored set at 270 playable points (290 with the granted backer badges), about 4.5% of the
 * maximum. It should stay small next to the
 * collection ladders; these mark the shape of a playthrough, not its bulk.
 *
 * THE REWARD DECOR IS DEALT BY PRESTIGE. Each `reward: true` piece in sim/decor.ts is paid by exactly one
 * achievement, and its weight follows the points: the 1–2 point starters pay a copy of a plain STATIC piece —
 * a shop wheelbarrow or rain barrel, or First Sprout's Carved Orb, the one static reward piece — never anything animated;
 * the animated pieces (twin lamp, wind spinner, the four coloured braziers) sit on 3–6 point goals
 * whose theme they match — frost on fishing, ember on land, amethyst on decor, gilt on check-ins; and the
 * Spectral Brazier and Crystal Obelisk belong to 7+ point long hauls. Moving an animated piece back onto a
 * starter is what made the opening hour pay more spectacle than the late game. *
 * THE SKINS ARE DEALT BY PRESTIGE TOO. Each reward pot, shelf and floor is paid by exactly one achievement,
 * and the more striking the skin, the harder the goal. Ranked glow/sparkle > patterned/ornate > recolour >
 * plain shape variant, the deal runs:
 *   pots    ghostlight 10 · prism 8 · ember 8 · blackGold 7 · verdigris 6 · porcelain 6 · loam 5 · mossy 4 ·
 *           midnight 4 · pedestal 3 · urn 2   (foundersGilt is the backer badge's, outside the deal)
 *   shelves verdigris 7 · walnut 5 · bamboo 4 · ironSlate 2 · whitewash 2 · pottingOak 1
 *   floors  hexEncaustic 10 · terrazzo 4 · cedarDeck 4 · herringbone 3 · slateMoss 3
 * with theme breaking ties: the water-blue porcelain on Waterscape, Walnut & Copper on the 100k purse, the
 * top shelf on the full greenhouse, cedar decking on the angler. The Round Urn is the terracotta pot with a
 * belly and nothing else, so it pays the earliest pot goal — never a long haul. Only ONE pot may sit on a
 * ≤ 2 point goal (smoke.mjs pins it): the early skins are meant to be a taste, not a wardrobe.
 *
 * THE PETALS FOLLOW THE POINTS, on one curve — every playable achievement here pays petals, sized by its points:
 *   points   1    2    3    4    5    6    7    8    9    10
 *   petals  10   20   35   50   75  100  150  200  300   400     (the Triple Crown and Complete Herbarium: 600)
 * Sized against the other two petal sources: the coin exchange pays at most 100 a day (server/exchange.js) and
 * a full check-in board ~1,180 a month, so a long haul is worth a day or two of exchanging and a completion
 * the best part of a week. THE CEILING: no single achievement may buy a shop item, even for a Garden Club member
 * — the cheapest product is `centsToPetals(299)` ≈ 2,062 and `grantPetals` triples every grant for a subscriber,
 * so the largest grant must stay under 2,062 / 3 ≈ 687 (achievements.test.ts pins it). The server's petal rate
 * limit is sized off the largest grant too (server/index.js MAX_ACHIEVEMENT_PETALS) — raise both together.
 * The old payouts (0–60, with 8-point goals paying 5) were a rounding error next to a day of exchanging.
 */
export const DEFAULT_ACHIEVEMENTS: AchievementDef[] = [
  {
    id: "first_harvest",
    category: "plants",
    points: 1,
    name: "First Sprout",
    desc: "Harvest your first plant.",
    icon: "🌱",
    conditions: [{ metric: "harvestCount", threshold: 1 }],
    badge: { style: "leafGreen", icon: "plant:rose" },
    reward: { coins: 50, petals: 10, decor: { carvedOrb: 1 }, unlocks: ["decor:carvedOrb"] },
  },
  // ---- THE LAND LADDER. `plotCells` counts the free starting cell, so these read as the number the
  // expansion tile shows: 5 plots, 15 plots, then 35 and the cap. Only the TOP rung is pinned to
  // `MAX_PLOT_CELLS`; the rungs below it are milestones on the way up and stay where they are when the cap
  // moves — but if the cap is ever lowered under 35, `estate` needs lowering with it. ----
  {
    id: "broke_ground",
    category: "garden",
    points: 1,
    name: "Broke Ground",
    desc: "Expand the garden to 5 plots.",
    icon: "🧱",
    conditions: [{ metric: "plotCells", threshold: 5 }],
    badge: { style: "plotEarth", icon: "decor:signpost" },
    reward: { coins: 200, petals: 10, decor: { wheelbarrow: 1 } },
  },
  {
    id: "first_shelf",
    category: "garden",
    points: 2,
    name: "Room to Grow",
    desc: "Buy a second greenhouse rack.",
    icon: "🪟",
    conditions: [{ metric: "shelfCount", threshold: 2 }],
    badge: { style: "glassPane", icon: "decor:coldFrame" },
    reward: { coins: 250, petals: 20, unlocks: ["shelf:whitewash"] },
  },
  // THE COMPOSTER — the first compost, a hundred of them, a rack's worth of good soil, and the first gold bag.
  {
    id: "first_compost",
    category: "garden",
    points: 1,
    name: "Back to the Soil",
    desc: "Make your first compost.",
    icon: "🟫",
    conditions: [{ metric: "compostsMade", threshold: 1 }],
    badge: { style: "compostLoam", icon: "decor:wheelbarrow" },
    reward: { coins: 150, petals: 10, unlocks: ["shelf:pottingOak"] },
  },
  {
    id: "compost_100",
    category: "garden",
    points: 3,
    name: "Heap Keeper",
    desc: "Make 100 composts.",
    icon: "🟫",
    conditions: [{ metric: "compostsMade", threshold: 100 }],
    badge: { style: "compostTurned", text: "100" },
    reward: { coins: 1500, petals: 35, unlocks: ["floor:slateMoss"] },
  },
  {
    id: "rich_soil",
    category: "garden",
    points: 3,
    name: "Rich Soil",
    desc: "Have 12 pots holding ★★★ compost or better.",
    icon: "🪴",
    conditions: [{ metric: "compostPotsThreePlus", threshold: 12 }],
    badge: { style: "compostHumus", icon: "decor:pottingBench" },
    reward: { coins: 1000, petals: 35, unlocks: ["floor:herringbone"] },
  },
  {
    id: "black_gold",
    category: "garden",
    points: 4,
    name: "Black Gold",
    desc: "Make a ★★★★★ compost.",
    icon: "✨",
    conditions: [{ metric: "bestCompostRank", threshold: 5 }],
    badge: { style: "compostGold", text: "5★" },
    reward: { coins: 2500, petals: 50, unlocks: ["pot:mossy"] },
  },
  {
    id: "dug_a_channel",
    category: "garden",
    points: 2,
    name: "Dug a Channel",
    desc: "Paint your first stretch of water: 200 cells.",
    icon: "💧",
    conditions: [{ metric: "waterCells", threshold: 200 }],
    badge: { style: "waterTeal", icon: "decor:waterPump" },
    reward: { coins: 250, petals: 20, decor: { rainBarrel: 1 } },
  },
  {
    id: "homely_touch",
    category: "garden",
    points: 2,
    name: "A Homely Touch",
    desc: "Have 10 decorations placed in the garden at once.",
    icon: "🏮",
    conditions: [{ metric: "decorPlaced", threshold: 10 }],
    badge: { style: "hearthAmber", icon: "decor:lantern" },
    reward: { coins: 250, petals: 20, decor: { hangingBasket: 1 } },
  },
  {
    id: "week_of_mornings",
    category: "garden",
    points: 2,
    name: "A Week of Mornings",
    desc: "Claim 7 daily check-ins.",
    icon: "🌅",
    conditions: [{ metric: "checkinDays", threshold: 7 }],
    badge: { style: "dawnViolet", text: "7" },
    reward: { coins: 300, petals: 20, decor: { birdhouse: 1 } },
  },
  {
    id: "green_thumb",
    category: "plants",
    points: 2,
    name: "Green Thumb",
    desc: "Harvest 25 plants.",
    icon: "🪴",
    conditions: [{ metric: "harvestCount", threshold: 25 }],
    badge: { style: "shieldGreen", icon: "plant:sunflower" },
    reward: { coins: 250, petals: 20, unlocks: ["pot:urn"] },
  },
  {
    id: "landholder",
    category: "garden",
    points: 4,
    name: "Landholder",
    desc: "Expand the garden to 15 plots.",
    icon: "🗺️",
    conditions: [{ metric: "plotCells", threshold: 15 }],
    badge: { style: "plotStone", icon: "decor:obelisk" },
    reward: { coins: 1200, petals: 50, decor: { brazierEmber: 1 }, unlocks: ["decor:brazierEmber", "fence:drystone"] },
  },
  {
    id: "three_shelves",
    category: "garden",
    points: 4,
    name: "Under Glass",
    desc: "Own 3 greenhouse racks.",
    icon: "🏡",
    conditions: [{ metric: "shelfCount", threshold: 3 }],
    badge: { style: "glassGable", icon: "decor:dovecote" },
    reward: { coins: 800, petals: 50, unlocks: ["pot:midnight"] },
  },
  {
    id: "well_stocked",
    category: "garden",
    points: 3,
    name: "Coin Purse",
    desc: "Hold 10,000 coins at once.",
    icon: "🪙",
    conditions: [{ metric: "coins", threshold: 10_000 }],
    badge: { style: "coinStack", text: "10K" },
    reward: { petals: 35, decor: { windSpinner: 1 }, unlocks: ["pot:pedestal", "decor:windSpinner"] },
  },
  {
    id: "fortnight",
    category: "garden",
    points: 4,
    name: "Fortnight",
    desc: "Claim 14 daily check-ins.",
    icon: "🗓️",
    conditions: [{ metric: "checkinDays", threshold: 14 }],
    badge: { style: "duskAmethyst", text: "14" },
    reward: { coins: 600, petals: 50, decor: { brazierGilt: 1 }, unlocks: ["floor:terrazzo", "decor:brazierGilt"] },
  },
  {
    id: "landscaper",
    category: "garden",
    points: 4,
    name: "Landscaper",
    desc: "Have 40 decorations placed in the garden at once.",
    icon: "🪑",
    conditions: [{ metric: "decorPlaced", threshold: 40 }],
    badge: { style: "terracottaStar", icon: "decor:gardenBench" },
    reward: { coins: 1000, petals: 50, decor: { brazierAmethyst: 1 }, unlocks: ["decor:brazierAmethyst", "fence:boxHedge"] },
  },
  {
    id: "still_waters",
    category: "garden",
    points: 4,
    name: "Still Waters",
    desc: "Paint 1,000 water cells, enough for a proper pond.",
    icon: "🏞️",
    conditions: [{ metric: "waterCells", threshold: 1000 }],
    badge: { style: "waterLily", icon: "decor:fountain" },
    reward: { coins: 900, petals: 50, decor: { stoneLantern: 1 }, unlocks: ["decor:stoneLantern"] },
  },
  {
    id: "master_gardener",
    category: "plants",
    points: 3,
    name: "Head Gardener",
    desc: "Harvest 150 plants.",
    icon: "🌷",
    // 3, not 6: 150 harvests is an hour or so on one or two racks — a session goal, not a long haul.
    conditions: [{ metric: "harvestCount", threshold: 150 }],
    badge: { style: "t4", text: "150" },
    reward: { coins: 1000, petals: 35, topMarkers: 5 },
  },
  {
    id: "botanist",
    category: "plants",
    points: 2,
    name: "Botanist",
    desc: "Harvest 10 different plant species.",
    icon: "🌸",
    // 2, not 4: two species unlock per level, so ten is around Gardening level 5 — the opening session.
    conditions: [{ metric: "plantSpecies", threshold: 10 }],
    badge: { style: "rosettePink", icon: "plant:prismRose" },
    reward: { coins: 400, petals: 20, unlocks: ["shelf:ironSlate"] },
  },
  {
    id: "angler",
    category: "fish",
    points: 4,
    name: "Angler",
    desc: "Catch 8 different fish species.",
    icon: "🎣",
    conditions: [{ metric: "fishSpecies", threshold: 8 }],
    badge: { style: "gemBlue", icon: "fish:koi" },
    reward: { coins: 400, petals: 50, fishXp: 200, unlocks: ["floor:cedarDeck"] },
  },
  {
    id: "bug_collector",
    category: "insects",
    points: 4,
    name: "Bug Collector",
    desc: "Collect 10 different insects.",
    icon: "🦋",
    conditions: [{ metric: "insectSpecies", threshold: 10 }],
    badge: { style: "hexWood", icon: "insect:luna" },
    reward: { coins: 400, petals: 50, bugXp: 200, decor: { twinLampPost: 1 }, unlocks: ["shelf:bamboo", "decor:twinLampPost"] },
  },
  // ---- THE MIDDLE RUNGS. Each fills a gap where a ladder used to jump straight from a first-session goal to a
  // completion (8 → 70 fish, 10 → 70 insects) or stop dead (150 harvests). Every reward skin and reward decor
  // piece is already dealt to exactly one achievement, so these pay coins, petals and XP only — points and
  // payouts interpolated from the rungs either side. ----
  {
    // Shelf #4 is Gardening level 14 and 1,800 coins (sim/gadgets.ts) — between Under Glass (L8) and the
    // full greenhouse (L22).
    id: "four_shelves",
    category: "garden",
    points: 5,
    name: "Glasshouse",
    desc: "Own 4 greenhouse racks.",
    icon: "🪴",
    conditions: [{ metric: "shelfCount", threshold: 4 }],
    badge: { style: "glassGable", icon: "decor:wateringCan" },
    reward: { coins: 2000, petals: 75 },
  },
  {
    // 1,000 is several sessions: a one-rack greenhouse turns ~120 harvests an hour (BALANCE.md §6, the composter
    // throughput check), a full one several hundred. A long step up from Head Gardener's hour-long 150.
    id: "harvest_1000",
    category: "plants",
    points: 5,
    name: "Bumper Crop",
    desc: "Harvest 1,000 plants.",
    icon: "🧺",
    conditions: [{ metric: "harvestCount", threshold: 1000 }],
    badge: { style: "t4", icon: "decor:terracottaPots", text: "1K" },
    reward: { coins: 3000, petals: 75, xp: 6000 },
  },
  {
    // 25 is more than one water's commons and uncommons together (7 + 9 per habitat — sim/fishing.ts THE WEIGHT
    // LADDER), so it needs both a pond and a stream, or a first run of rares.
    id: "fish_species_25",
    category: "fish",
    points: 6,
    name: "Two Waters",
    desc: "Catch 25 different fish species.",
    icon: "🐠",
    conditions: [{ metric: "fishSpecies", threshold: 25 }],
    badge: { style: "t3", icon: "fish:grayling", text: "25" },
    reward: { coins: 2500, petals: 100, fishXp: 600 },
  },
  {
    // 25 is every common (22 — sim/insects.ts THE TIER SPREAD) and the first few uncommons.
    id: "insect_species_25",
    category: "insects",
    points: 6,
    name: "Field Notes",
    desc: "Collect 25 different insects.",
    icon: "🐝",
    conditions: [{ metric: "insectSpecies", threshold: 25 }],
    badge: { style: "t3", icon: "insect:swallowtail", text: "25" },
    reward: { coins: 2500, petals: 100, bugXp: 400 },
  },
  {
    // KICKSTARTER BACKER REWARD. Granted by ENTITLEMENT, never by play — its conditions list is empty, and
    // `evaluateAchievements` treats an achievement with no conditions as one that can never unlock, so no amount
    // of gardening earns it. The redemption path adds the id to `unlockedAchievements` directly.
    // `granted: true` keeps it out of the ladder entirely — not even a redacted row — for everyone who does not
    // have it (visibleAchievements), so it never reads as a goal a player has failed to reach.
    id: "founder",
    category: "garden",
    points: 10,
    name: "Founder",
    desc: "Backed Shelf Garden on Kickstarter.",
    icon: "\u{1F3C5}",
    conditions: [],
    badge: { style: "founder", text: "FOUNDER" },
    reward: { unlocks: ["pot:foundersGilt"] },
    granted: true,
  },
  {
    // KICKSTARTER COLLECTOR TIER — the top backer reward, granted the same way as `founder` (a redeem code
    // carries it; no conditions, so play never unlocks it; `granted` hides it from everyone who does not hold
    // it). A Collector's code grants BOTH, so their case shows two medals: FOUNDER and PATRON (badge style
    // `founderPatron`, the Founder medal's senior sibling — sim/badges.ts). Points match `founder`.
    //
    // NO UNLOCKS. The Collector's Void Anodized pot is granted ON ITS OWN from the admin account page's Kickstarter
    // panel ("pot:voidAnodized", sim/backerCosmetics.ts), like every other backer reward, so this badge does not
    // need to carry it. Do not point this at an existing reward skin: every one of those is a playable prize.
    id: "founder_collector",
    category: "garden",
    points: 10,
    name: "Founding Patron",
    desc: "Backed Shelf Garden on Kickstarter at the Collector tier.",
    icon: "\u{1F451}",
    conditions: [],
    badge: { style: "founderPatron", text: "PATRON" },
    granted: true,
  },
  {
    id: "seasoned_grower",
    category: "plants",
    points: 5,
    name: "Seasoned Grower",
    desc: "Reach Gardening level 10.",
    icon: "⭐",
    conditions: [{ metric: "gardenLevel", threshold: 10 }],
    badge: { style: "medalSky", text: "10" },
    reward: { coins: 750, petals: 75, unlocks: ["pot:loam"] },
  },
  // ---- THE SKILL LADDER, rungs 10 and 25. Every track climbs 10 → 25 → 50; the gardening and bug 25s pay two
  // reward avatar borders, fishing_50 pays the Hoarfrost and the triple crown below pays the fourth. These rungs wear the plain tier emblems (t2 for
  // 10, t3 for 25, t5 for the crown) rather than landmark styles — they ARE rungs, so they read as rungs. ----
  {
    id: "fishing_10",
    category: "fish",
    points: 5,
    name: "Steady Line",
    desc: "Reach Fishing level 10.",
    icon: "🎣",
    conditions: [{ metric: "fishingLevel", threshold: 10 }],
    badge: { style: "t2", icon: "fish:brookTrout", text: "10" },
    reward: { coins: 750, petals: 75, fishXp: 250 },
  },
  {
    id: "naturalist_10",
    category: "insects",
    points: 5,
    name: "Keen Eye",
    desc: "Reach Bug Catching level 10.",
    icon: "🔎",
    conditions: [{ metric: "bugLevel", threshold: 10 }],
    badge: { style: "t2", icon: "insect:firefly", text: "10" },
    reward: { coins: 750, petals: 75, bugXp: 250 },
  },
  {
    id: "gardening_25",
    category: "plants",
    points: 6,
    name: "Gilded Green Thumb",
    desc: "Reach Gardening level 25.",
    icon: "🌼",
    conditions: [{ metric: "gardenLevel", threshold: 25 }],
    badge: { style: "t3", icon: "plant:goldenCactus", text: "25" },
    reward: { coins: 5000, petals: 100, unlocks: ["frame:gilded"] },
  },
  {
    // The Frost Brazier is the cold-water fire, so it hangs off the Frostdart's track.
    id: "fishing_25",
    category: "fish",
    points: 6,
    name: "Cold Water Angler",
    desc: "Reach Fishing level 25.",
    icon: "❄️",
    conditions: [{ metric: "fishingLevel", threshold: 25 }],
    badge: { style: "t3", icon: "fish:frostdart", text: "25" },
    reward: { coins: 5000, petals: 100, decor: { brazierFrost: 1 }, unlocks: ["decor:brazierFrost"] },
  },
  {
    // Aurora — night fliers under a lit sky.
    id: "naturalist_25",
    category: "insects",
    points: 6,
    name: "Night Flier",
    desc: "Reach Bug Catching level 25.",
    icon: "🌌",
    conditions: [{ metric: "bugLevel", threshold: 25 }],
    badge: { style: "t3", icon: "insect:auroralace", text: "25" },
    reward: { coins: 5000, petals: 100, unlocks: ["frame:aurora"] },
  },
  // ---- LATE GAME. Everything below here is a long haul or a completion. ----
  {
    id: "moneyed",
    category: "garden",
    points: 5,
    name: "Moneyed",
    desc: "Hold 100,000 coins at once.",
    icon: "💰",
    conditions: [{ metric: "coins", threshold: 100_000 }],
    badge: { style: "coinBanner", text: "100K" },
    reward: { petals: 75, unlocks: ["shelf:walnut"] },
  },
  {
    id: "full_greenhouse",
    category: "garden",
    points: 7,
    name: "Full Greenhouse",
    desc: `Own all ${MAX_RACKS} greenhouse racks.`,
    icon: "🌿",
    // Reads MAX_RACKS (sim/gadgets.ts) so the cap and this threshold cannot part — a completion achievement
    // that unlocks one shelf short of full is worse than no achievement at all.
    conditions: [{ metric: "shelfCount", threshold: MAX_RACKS }],
    badge: { style: "glassCrown", icon: "decor:coldFrame" },
    reward: { coins: 4000, petals: 150, unlocks: ["shelf:verdigris"] },
  },
  {
    id: "waterscape",
    category: "garden",
    points: 6,
    name: "Waterscape",
    desc: "Paint 4,000 water cells, enough for streams and pools across the whole garden.",
    icon: "🌊",
    conditions: [{ metric: "waterCells", threshold: 4000 }],
    badge: { style: "waterDeep", icon: "decor:wishingWell" },
    reward: { coins: 3000, petals: 100, unlocks: ["pot:porcelain"] },
  },
  {
    id: "furnished",
    category: "garden",
    points: 6,
    name: "Fully Furnished",
    desc: "Have 100 decorations placed in the garden at once.",
    icon: "🏵️",
    conditions: [{ metric: "decorPlaced", threshold: 100 }],
    badge: { style: "verdigris", icon: "decor:armillary" },
    reward: { coins: 3000, petals: 100, unlocks: ["building:summerhouse"] },
  },
  {
    id: "full_board",
    category: "garden",
    points: 7,
    name: "The Full Board",
    // One whole board — CHECKIN_DAYS squares (sim/checkin.ts), so the name stays true if the board is resized.
    desc: `Claim ${CHECKIN_DAYS} daily check-ins.`,
    icon: "🌙",
    conditions: [{ metric: "checkinDays", threshold: CHECKIN_DAYS }],
    badge: { style: "moonPearl", text: String(CHECKIN_DAYS) },
    reward: { coins: 2500, petals: 150, unlocks: ["building:cedarWorkshop"] },
  },
  {
    id: "petal_hoard",
    category: "garden",
    points: 6,
    name: "Petal Hoard",
    desc: "Hold 1,500 Petals at once.",
    icon: "🌺",
    // 1,500, not 250: the exchange alone (100/day) and the first week of check-ins and achievements reach 250
    // inside a few days, which is no 6-point long haul. 1,500 is a couple of weeks of saving rather than
    // spending — and still short of the cheapest shop item (~2,062), so it marks the saving, not a purchase.
    conditions: [{ metric: "petals", threshold: 1_500 }],
    badge: { style: "petalMythic", text: "1.5K" },
    reward: { petals: 100, unlocks: ["pot:verdigris"] },
  },
  {
    id: "curator",
    category: "plants",
    points: 7,
    name: "Curator",
    desc: "Harvest 50 different plant species.",
    icon: "📗",
    conditions: [{ metric: "plantSpecies", threshold: 50 }],
    badge: { style: "cabinetBrass", icon: "plant:voidOrchid" },
    reward: { coins: 3000, petals: 150, decor: { brazierSpectral: 1 }, unlocks: ["pot:blackGold", "decor:brazierSpectral"] },
  },
  {
    // 45 of 70: commons, uncommons and rares together are 48 (sim/fishing.ts THE WEIGHT LADDER), so this is most
    // of the rares in BOTH waters — a long haul inside the ~35 h fishing climb, with the very rares and the
    // legendaries left for Every Fish in the Water.
    id: "fish_species_45",
    category: "fish",
    points: 8,
    name: "The Long Cast",
    desc: "Catch 45 different fish species.",
    icon: "🐡",
    conditions: [{ metric: "fishSpecies", threshold: 45 }],
    badge: { style: "t4", icon: "fish:mirrorpike", text: "45" },
    reward: { coins: 10_000, petals: 200, fishXp: 2000 },
  },
  {
    // 45 of 70: commons + uncommons are 37 (sim/insects.ts THE TIER SPREAD), so this is eight of the seventeen
    // rares on top — the epics and legendaries stay with Every Wing and Shell.
    id: "insect_species_45",
    category: "insects",
    points: 8,
    name: "Specimen Drawers",
    desc: "Collect 45 different insects.",
    icon: "🪲",
    conditions: [{ metric: "insectSpecies", threshold: 45 }],
    badge: { style: "t4", icon: "insect:morpho", text: "45" },
    reward: { coins: 10_000, petals: 200, bugXp: 1000 },
  },
  {
    id: "estate",
    category: "garden",
    points: 8,
    name: "The Estate",
    desc: "Expand the garden to 35 plots.",
    icon: "🌳",
    conditions: [{ metric: "plotCells", threshold: 35 }],
    badge: { style: "plotEstate", icon: "decor:obelisk" },
    reward: { coins: 8000, petals: 200, unlocks: ["pot:ember", "fence:wroughtIron"] },
  },
  {
    id: "vault",
    category: "garden",
    points: 8,
    name: "The Vault",
    desc: "Hold 1,000,000 coins at once.",
    icon: "🏦",
    conditions: [{ metric: "coins", threshold: 1_000_000 }],
    badge: { style: "vaultAmber", text: "1M" },
    reward: { petals: 200, unlocks: ["pot:prism"] },
  },
  // ---- THE COMPLETIONS. Nothing sits above any of these; 9 and 10 exist for exactly this row. ----
  {
    id: "whole_garden",
    category: "garden",
    points: 10,
    name: "The Whole Garden",
    desc: `Own all ${MAX_PLOT_CELLS} garden plots. There is no more ground to buy.`,
    icon: "🏆",
    // THE THRESHOLD IS THE CAP ITSELF, read from sim/garden.ts rather than written out — 50 is an
    // expansion-facing number that is expected to move. Hard-coding it here is how a completion quietly
    // becomes unreachable (cap lowered below the achievement) or stops reading as a completion (cap raised above
    // it). The name and badge text follow the same constant, so the whole row moves in one edit.
    conditions: [{ metric: "plotCells", threshold: MAX_PLOT_CELLS }],
    badge: { style: "plotGold", text: String(MAX_PLOT_CELLS) },
    reward: { coins: 25_000, petals: 400, decor: { crystalObelisk: 1 }, unlocks: ["decor:crystalObelisk"] },
  },
  {
    id: "gardening_50",
    category: "plants",
    points: 10,
    name: "Grand Horticulturist",
    desc: "Reach Gardening level 50.",
    icon: "🎖️",
    conditions: [{ metric: "gardenLevel", threshold: 50 }],
    badge: { style: "trackGreen", text: "50" },
    reward: { coins: 20_000, petals: 400, unlocks: ["building:palmHouse"] },
  },
  {
    // Hoarfrost moved up from fishing_25: the 50s on the other two tracks pay a greenhouse building, and
    // fishing has none, so the border is this rung's one-of-a-kind prize rather than a brazier recolour.
    id: "fishing_50",
    category: "fish",
    points: 10,
    name: "Master Angler",
    desc: "Reach Fishing level 50.",
    icon: "🐟",
    conditions: [{ metric: "fishingLevel", threshold: 50 }],
    badge: { style: "trackBlue", text: "50" },
    reward: { coins: 20_000, petals: 400, unlocks: ["frame:frost"] },
  },
  {
    id: "naturalist_50",
    category: "insects",
    points: 10,
    name: "Master Bug Catcher",
    desc: "Reach Bug Catching level 50.",
    icon: "🐞",
    conditions: [{ metric: "bugLevel", threshold: 50 }],
    badge: { style: "trackAmber", text: "50" },
    reward: { coins: 20_000, petals: 400, unlocks: ["building:moonlit"] },
  },
  {
    // THE TRIPLE CROWN — all three tracks topped out. The laurel is the victor's prize, so the Golden Laurel
    // border lives here, on the hardest of the skill achievements, and nowhere else.
    id: "triple_crown",
    category: "garden",
    points: 10,
    name: "The Triple Crown",
    desc: "Reach level 50 in Gardening, Fishing and Bug Catching.",
    icon: "👑",
    conditions: [
      { metric: "gardenLevel", threshold: 50 },
      { metric: "fishingLevel", threshold: 50 },
      { metric: "bugLevel", threshold: 50 },
    ],
    badge: { style: "t5", icon: "decor:brazierGilt" },
    reward: { coins: 30_000, petals: 600, unlocks: ["frame:laurel"] },
  },
  {
    // All 70 ARE catchable on a free account: the Bog Wraith, Moonscale and Frostdart used to sit behind `all`
    // host-set recipes naming premium (shop-only) pond plants, which quietly made this achievement a purchase. Those
    // sets now carry a non-premium path (hostSets.ts, THE PREMIUM RULE) — keep it that way rather than lowering
    // the threshold, or this 10-pointer goes back to being paid.
    id: "full_aquarium",
    category: "fish",
    points: 10,
    name: "Every Fish in the Water",
    desc: `Catch all ${FISH.length} fish species.`,
    icon: "🌐",
    conditions: [{ metric: "fishSpecies", threshold: FISH.length }],
    badge: { style: "aquariumGlass", icon: "fish:aetherling" },
    reward: { coins: 25_000, petals: 400, unlocks: ["pot:ghostlight"] },
  },
  {
    // Same story as full_aquarium: the Voidwing and Auroralace were premium-gated by their host sets, so this
    // achievement was too. Both have a levelling-reachable path now; do not re-gate them.
    id: "full_cabinet",
    category: "insects",
    points: 10,
    name: "Every Wing and Shell",
    desc: `Collect all ${INSECTS.length} insect species.`,
    icon: "🗄️",
    conditions: [{ metric: "insectSpecies", threshold: INSECTS.length }],
    badge: { style: "chitinCase", icon: "insect:voidwing" },
    reward: { coins: 25_000, petals: 400, unlocks: ["floor:hexEncaustic"] },
  },
  {
    // THE ONE ACHIEVEMENT THAT GENUINELY INCLUDES PAID CONTENT, and the desc says so rather than pretending otherwise.
    // 9 of the 109 are PREMIUM plants (PREMIUM_PLANTS in level.ts) which `isUnlocked` never grants by levelling —
    // unlike a creature, a plant species has no alternative route, so there is nothing to un-gate. Lowering the
    // threshold to 100 was the alternative and it is worse: it would retire the word "complete" and leave the
    // nine best plants worth no progress at all. Stating the cost keeps the promise honest.
    id: "complete_herbarium",
    category: "plants",
    points: 10,
    name: "The Complete Herbarium",
    desc: `Harvest all ${ALL_PLANT_IDS.length} plant species, including the ${PREMIUM_PLANT_IDS.length} premium plants.`,
    icon: "📚",
    conditions: [{ metric: "plantSpecies", threshold: ALL_PLANT_IDS.length }],
    badge: { style: "herbarium", icon: "plant:auroraFern" },
    reward: { coins: 30_000, petals: 600, unlocks: ["building:crystalConservatory"] },
  },
];

// ---------------------------------------------------------------------------
//  LIVE (DB-backed) overlay
// ---------------------------------------------------------------------------

/** Per-id overrides installed from the `achievements` tuning key. A `null` entry DISABLES that achievement. */
let LIVE: Record<string, AchievementDef | null> = {};
/** Achievements that exist ONLY in the live blob (authored in the dev menu, no source default). */
let LIVE_ORDER: string[] = [];

/** A reward COUNT, coerced: a finite non-negative whole number, or 0 for anything else. The fields this
 *  guards are the ones that mutate the economy — `setCoins((c) => c + g.coins)` on a string `"500"` makes
 *  the balance the string `"0500"`, and a NaN makes it NaN for the rest of the session — so every numeric
 *  field of a tuning-authored reward goes through here before it can reach a setter. */
function rewardCount(v: unknown): number {
  return typeof v === "number" && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0;
}

/** Coerce an id → count map (seeds, decor) from a tuning blob. Non-object input becomes undefined; entries
 *  whose count does not survive `rewardCount` are DROPPED, so the map that reaches the grant path contains
 *  only payable amounts. The ids themselves are NOT validated here — a retired id must stay visible to the
 *  editors' `unknownDecorKeys` check; the grant path is what refuses to bank it. */
function rewardCounts<K extends string>(v: unknown): Partial<Record<K, number>> | undefined {
  if (!v || typeof v !== "object" || Array.isArray(v)) return undefined;
  const out: Partial<Record<K, number>> = {};
  for (const [k, n] of Object.entries(v as Record<string, unknown>)) {
    const c = rewardCount(n);
    if (c) out[k as K] = c;
  }
  return out;
}

/**
 * Coerce a whole reward from the tuning blob. THE ONE FIELD THAT MOVES THE ECONOMY was also the one the
 * rest of `normalise` did not cover: a reward object was passed through as-authored on the strength of
 * `typeof o.reward === "object"` alone, so `{ coins: "500" }` or `{ petals: NaN }` travelled intact from
 * Postgres to `setCoins`. Every field is now typed on the way in, and a reward that ends up granting
 * nothing at all resolves to undefined so `grantOf` skips it rather than paying zeroes.
 *
 * EXPORTED because the point track's own `normalise` (sim/pointTrack.ts) coerces the same reward shape off
 * the same untrusted tuning source. One copy of the rules, so the two claim paths cannot drift on what a
 * payable amount is — the same reason `milestoneGrant` mirrors `grantOf`.
 */
export function normaliseReward(raw: unknown, base: AchievementReward | undefined): AchievementReward | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return base;
  const o = raw as Record<string, unknown>;
  const r: AchievementReward = {};
  if (typeof o.potSkin === "string" && o.potSkin) r.potSkin = o.potSkin;
  if (Array.isArray(o.unlocks)) {
    const u = o.unlocks.filter((x): x is string => typeof x === "string" && /^(pot|building|floor|shelf|decor|frame|fence):./.test(x));
    if (u.length) r.unlocks = [...new Set(u)];
  }
  const coins = rewardCount(o.coins);   if (coins) r.coins = coins;
  const topMarkers = rewardCount(o.topMarkers); if (topMarkers) r.topMarkers = topMarkers;
  const petals = rewardCount(o.petals); if (petals) r.petals = petals;
  const xp = rewardCount(o.xp);         if (xp) r.xp = xp;
  const fishXp = rewardCount(o.fishXp); if (fishXp) r.fishXp = fishXp;
  const bugXp = rewardCount(o.bugXp);   if (bugXp) r.bugXp = bugXp;
  const seeds = rewardCounts<FlowerId>(o.seeds);
  if (seeds && Object.keys(seeds).length) r.seeds = seeds;
  const decor = rewardCounts<string>(o.decor);
  if (decor && Object.keys(decor).length) r.decor = decor as Record<string, number>;
  return Object.keys(r).length ? r : undefined;
}

/**
 * THE CEILING ON ONE ACHIEVEMENT'S PETALS — the server's `MAX_ACHIEVEMENT_PETALS` (server/index.js), restated
 * because the server is plain JS. The server sizes its petal rate limit off it, so a live `achievements` edit
 * paying more than this would be clamped on the server: the celebration shows the grant and the next save
 * quietly takes it back. A tuned reward is therefore clamped HERE, at install, and the editor's Petals box
 * stops at the same number (RewardEditor `maxPetals`). The source defaults top out exactly at it (the Triple
 * Crown and the Complete Herbarium). Raising it means raising the server's copy in the same change
 * (serverMirrors.test.ts), and it must stay under a shop item at the subscriber multiplier
 * (achievements.test.ts).
 */
export const MAX_ACHIEVEMENT_PETALS = 600;

/** `r` with its petals held to MAX_ACHIEVEMENT_PETALS. */
function capPetals(r: AchievementReward | undefined): AchievementReward | undefined {
  return r?.petals !== undefined && r.petals > MAX_ACHIEVEMENT_PETALS ? { ...r, petals: MAX_ACHIEVEMENT_PETALS } : r;
}

/** Coerce a raw tuning entry into a usable AchievementDef, falling back to the source default field by field.
 *  Anything malformed keeps the default rather than throwing — a bad blob must never break the game. */
function normalise(raw: unknown, base: AchievementDef | undefined, id: string): AchievementDef | null {
  if (raw === null) return null;
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  // A metric is valid if it is a known fixed key OR a parameterized "family:id" — the latter cannot be
  // checked against a list, since the whole point is that they are generated rather than enumerated.
  const okMetric = (m: unknown): m is AchievementMetric =>
    typeof m === "string" && (m.includes(":") || METRIC_IDS.includes(m as FixedMetric));
  const conds = Array.isArray(o.conditions)
    ? o.conditions
        .filter((c): c is Record<string, unknown> => !!c && typeof c === "object")
        .map((c) => ({
          metric: (okMetric(c.metric) ? c.metric : "harvestCount") as AchievementMetric,
          threshold: typeof c.threshold === "number" && Number.isFinite(c.threshold) ? c.threshold : 1,
          cmp: c.cmp === "atMost" ? ("atMost" as const) : ("atLeast" as const),
        }))
    : base?.conditions;
  const badgeRaw = (o.badge && typeof o.badge === "object" ? o.badge : null) as Record<string, unknown> | null;
  const badge: BadgeRef | undefined = badgeRaw && typeof badgeRaw.style === "string"
    ? {
        style: badgeRaw.style,
        icon: typeof badgeRaw.icon === "string" ? badgeRaw.icon : undefined,
        text: typeof badgeRaw.text === "string" ? badgeRaw.text : undefined,
        iconScale: typeof badgeRaw.iconScale === "number" ? badgeRaw.iconScale : undefined,
        iconOffsetY: typeof badgeRaw.iconOffsetY === "number" ? badgeRaw.iconOffsetY : undefined,
      }
    : base?.badge;
  return {
    id,
    name: typeof o.name === "string" && o.name ? o.name : base?.name ?? id,
    desc: typeof o.desc === "string" ? o.desc : base?.desc ?? "",
    icon: typeof o.icon === "string" && o.icon ? o.icon : base?.icon ?? "🏆",
    // A category that is not one of the four is DROPPED rather than kept, so a typo in the tuning blob
    // leaves the achievement to `categoryOf`'s inference instead of creating a fifth bucket no filter offers.
    category: ACHIEVEMENT_CATEGORIES.includes(o.category as AchievementCategory)
      ? (o.category as AchievementCategory)
      : base?.category,
    conditions: conds,
    reward: capPetals(normaliseReward(o.reward, base?.reward)),
    badge,
    secret: typeof o.secret === "boolean" ? o.secret : base?.secret,
    granted: typeof o.granted === "boolean" ? o.granted : base?.granted,
    points: typeof o.points === "number" ? o.points : base?.points,
    enabled: typeof o.enabled === "boolean" ? o.enabled : base?.enabled,
  };
}

/**
 * THE AUTHORED definition of every achievement the bundle ships — the hand-written ladder AND the generated
 * families — keyed by id, ignoring any live override. It is the base a live entry is normalised over, the
 * baseline the editor diffs against, and the "authored" side of the dev override report.
 *
 * GENERATED IDS NEED THEIR GENERATED BASE. The overlay used to look bases up in DEFAULT_ACHIEVEMENTS alone, so a
 * live entry for a generated id was normalised over nothing: an entry that set only `points` or `name` came out
 * with no conditions and no reward — an achievement that can never unlock, standing in for one that could.
 */
export function authoredAchievementBases(): Map<string, AchievementDef> {
  const bases = new Map(DEFAULT_ACHIEVEMENTS.map((a) => [a.id, a]));
  for (const gen of GENERATORS) for (const a of gen()) if (!bases.has(a.id)) bases.set(a.id, a);
  return bases;
}

/** Install the live achievement overlay (called at boot + on every tuning refresh; see main.tsx). A `null`
 *  entry DISABLES that id; an entry that is neither null nor an object (a number, a string, an array) is
 *  IGNORED, so the authored definition stands — a stray value must not mint a nameless achievement. */
export function setAchievementOverrides(o: Record<string, unknown> | null | undefined): void {
  const next: Record<string, AchievementDef | null> = {};
  const order: string[] = [];
  if (o && typeof o === "object" && !Array.isArray(o)) {
    const bases = authoredAchievementBases();
    for (const [id, raw] of Object.entries(o)) {
      if (raw !== null && (typeof raw !== "object" || Array.isArray(raw))) continue;
      next[id] = normalise(raw, bases.get(id), id);
      if (!bases.has(id) && next[id]) order.push(id);
    }
  }
  LIVE = next;
  LIVE_ORDER = order;
  rebuild();
}

/** The effective ladder: source defaults with live overrides applied, then any dev-authored extras appended.
 *  Rebuilt only when the overlay changes, so the common read path is a plain array. */
export let ACHIEVEMENTS: AchievementDef[] = [...DEFAULT_ACHIEVEMENTS];
/** Family groupings, lazily built by `familyMembers` and cleared by `rebuild`. Declared beside the ladder
 *  it indexes so the two cannot fall out of step. */
let FAMILY_INDEX: Map<string, AchievementDef[]> | null = null;
let BY_ID = new Map(ACHIEVEMENTS.map((a) => [a.id, a]));

function rebuild(): void {
  const out: AchievementDef[] = [];
  const seen = new Set<string>();
  const push = (base: AchievementDef) => {
    const ov = LIVE[base.id];
    if (ov === null) return;                    // explicitly removed from the live ladder
    out.push(ov ?? base);
    seen.add(base.id);
  };
  // Hand-authored milestones first — they are the headline ladder a new player sees.
  for (const base of DEFAULT_ACHIEVEMENTS) push(base);
  // Then the GENERATED families (the per-plant harvest ladder). These are derived from a rule rather than
  // stored, so the tuning blob stays ~1KB no matter how many thousands of achievements exist; a live override
  // still wins per id, which is what makes them editable in the dev menu without storing all of them.
  for (const gen of GENERATORS) for (const base of gen()) push(base);
  // Finally any achievement that exists ONLY in the live blob (authored in the dev menu).
  for (const id of LIVE_ORDER) {
    if (seen.has(id)) continue;
    const extra = LIVE[id];
    if (extra) out.push(extra);
  }
  ACHIEVEMENTS = out;
  BY_ID = new Map(out.map((a) => [a.id, a]));
  FAMILY_INDEX = null;                          // the ladder moved; the family grouping is stale with it
}

/**
 * Registered achievement GENERATORS. Registered at boot rather than imported directly, because the generators
 * import AchievementDef from this module — a direct import back would be a cycle. main.tsx calls
 * `registerAchievementGenerator(generatedAchievements)` once.
 */
type Generator = () => AchievementDef[];
const GENERATORS: Generator[] = [];

/** Add a family generator and rebuild the ladder. Idempotent per function reference. */
export function registerAchievementGenerator(gen: Generator): void {
  if (GENERATORS.includes(gen)) return;
  GENERATORS.push(gen);
  rebuild();
}

export function achievement(id: string): AchievementDef | undefined { return BY_ID.get(id); }

/** Resolve an achievement's badge into the flat object <BadgeArt> draws. Undefined when it has no badge. */
export function achievementBadge(a: AchievementDef): ResolvedBadge | undefined {
  return resolveBadge(a.badge, { id: a.id, name: a.name });
}

// ---------------------------------------------------------------------------
//  EVALUATION
// ---------------------------------------------------------------------------

/** An achievement's conditions (an achievement with none can never unlock — see evaluateAchievements). */
export function conditionsOf(a: AchievementDef): AchievementCondition[] {
  return a.conditions ?? [];
}

/** Read any metric — fixed field or parameterized "family:id" — off the snapshot. */
export function readMetric(stats: AchievementStats, metric: AchievementMetric): number {
  if (metric.includes(":")) return stats.counters?.[metric] ?? 0;
  const v = (stats as unknown as Record<string, unknown>)[metric];
  return typeof v === "number" ? v : 0;
}

/** Is one condition satisfied by this stats snapshot? */
function meets(c: AchievementCondition, stats: AchievementStats): boolean {
  const v = readMetric(stats, c.metric);
  return c.cmp === "atMost" ? v <= c.threshold : v >= c.threshold;
}

/** Progress 0..1 toward an achievement given the current stats (for a progress bar in the UI). With several
 *  conditions this is the MEAN of each one's progress, so a two-part goal shows as half done when one half
 *  is met — which reads more honestly than showing the worst leg alone. */
export function achievementProgress(a: AchievementDef, stats: AchievementStats): number {
  const cs = conditionsOf(a);
  if (!cs.length) return 0;
  let sum = 0;
  for (const c of cs) {
    const v = readMetric(stats, c.metric);
    if (c.cmp === "atMost") sum += v <= c.threshold ? 1 : 0;
    else sum += c.threshold <= 0 ? 1 : Math.max(0, Math.min(1, v / c.threshold));
  }
  return Math.max(0, Math.min(1, sum / cs.length));
}

/** Pure: the set of achievement ids that are unlocked at this stats snapshot. */
export function evaluateAchievements(stats: AchievementStats): Set<string> {
  const out = new Set<string>();
  for (const a of ACHIEVEMENTS) {
    if (a.enabled === false) continue;
    if (isOut(a)) continue;                     // its species is not out yet (sim/releases.ts)
    const cs = conditionsOf(a);
    if (!cs.length) continue;                   // an achievement with no conditions can never unlock
    if (cs.every((c) => meets(c, stats))) out.add(a.id);
  }
  return out;
}

/**
 * THE achievements a player should SEE, in ladder order. Every display surface (the in-game Achievements modal,
 * the profile badge case, the earned-count denominator) goes through this rather than reading `ACHIEVEMENTS`
 * directly — that duplication is what let `secret` end up honoured on the profile page but ignored in the
 * Achievements modal, and let disabled achievements show as permanently-locked slots nobody can earn.
 *
 * DISABLED achievements are hidden unless already earned. Hiding an earned one would silently strip a badge the
 * player has already been shown, so a RETIRED achievement stays in their case.
 *
 * SECRET achievements are NOT filtered here — they stay in the ladder so the player can see that something is
 * there to find; it is their NAME and DESCRIPTION that must be redacted until earned (see `isRedacted`).
 * They are, however, dropped from the profile CASE, which is a display of what you own rather than a
 * checklist of what is left.
 *
 * GRANTED achievements (backer rewards, promo codes) are hidden EVERYWHERE until granted. A secret is a tease; a
 * grant is not obtainable by playing, so showing it would only advertise something the player cannot have.
 */
export function visibleAchievements(unlocked: ReadonlySet<string>, opts: { hideSecret?: boolean } = {}): AchievementDef[] {
  return ACHIEVEMENTS.filter((a) => {
    const earned = unlocked.has(a.id);
    if (a.enabled === false && !earned) return false;
    if (a.granted && !earned) return false;                 // never advertise an ungranted backer reward
    if (isOut(a) && !earned) return false;                  // nor one for a species whose release is not out
    if (opts.hideSecret && a.secret && !earned) return false;
    return true;
  });
}

// ---------------------------------------------------------------------------
//  FAMILIES — "what else is on this achievement's ladder"
// ---------------------------------------------------------------------------

/**
 * THE FAMILY KEY: the rungs of one species ladder share it, and nothing else does. For a generated achievement
 * it is exactly its parameterized metric — `harvested:rose`, `fishCaught:rainbowTrout` — because that
 * string already names the metric AND the species, which is the whole definition of a family: same thing
 * counted, different thresholds.
 *
 * DERIVED FROM THE CONDITION, NOT PARSED OUT OF THE ID. The ids are `harvested_rose_500`, and splitting
 * one back apart means guessing where the species name ends — safe only while no species id contains an
 * underscore, which is a property of today's roster rather than a rule anyone agreed to. The condition
 * carries the same two facts already delimited, so this cannot be broken by naming a plant `string_of_pearls`.
 * NOTHING HERE TOUCHES THE IDS. They stay exactly what `harvestAchievementId` and friends produce, which is what
 * keeps dev-menu overrides and players' unlocked lists valid across a roster change (see the header of
 * sim/achievementFamilies.ts).
 *
 * Achievements with no parameterized metric — every hand-authored landmark — have NO family. They are one-offs by
 * design, and grouping "The Vault" with "Moneyed" because both read `coins` would invent a ladder that was
 * never authored as one.
 */
export function familyKeyOf(a: AchievementDef): string | undefined {
  for (const c of conditionsOf(a)) if (c.metric.includes(":")) return c.metric;
  return undefined;
}

/**
 * Every achievement on the same ladder as this one, INCLUDING the achievement itself, ordered by threshold — which is
 * the order the rungs were authored in and the order a related-achievements panel wants to show them.
 *
 * Returns the single-element list for an achievement with no family, so a caller never has to special-case a
 * landmark; a panel showing one row is the honest answer for an achievement that genuinely has no siblings.
 *
 * Reads the LIVE ladder (`ACHIEVEMENTS`), not the generators, so a rung disabled or retuned in the dev menu
 * is reflected here too, and a dev-authored achievement built on an existing metric joins the family it belongs to.
 */
export function familyOf(a: AchievementDef): AchievementDef[] {
  const key = familyKeyOf(a);
  if (!key) return [a];
  return familyMembers(key);
}

/** The achievements on one ladder, by family key, ordered by threshold. Built lazily on first ask and thrown away
 *  whenever the ladder rebuilds, so a modal opening on 5 rungs never costs a scan of all 1,296 achievements more
 *  than once per tuning refresh. */
export function familyMembers(key: string): AchievementDef[] {
  let index = FAMILY_INDEX;
  if (!index) {
    index = new Map<string, AchievementDef[]>();
    for (const a of ACHIEVEMENTS) {
      const k = familyKeyOf(a);
      if (!k) continue;
      const list = index.get(k);
      if (list) list.push(a);
      else index.set(k, [a]);
    }
    // Ladder order, not ACHIEVEMENTS order: a live override that renumbers a rung's threshold must move it
    // in the panel too, or the "next rung" a player reads off it is the wrong one.
    for (const list of index.values()) list.sort((x, y) => familyThreshold(x) - familyThreshold(y));
    FAMILY_INDEX = index;
  }
  return index.get(key) ?? [];
}

/** The threshold a family member sits at — its parameterized condition's, which is the number that makes
 *  one rung higher than another. Falls back to 0 so an odd achievement still sorts deterministically. */
function familyThreshold(a: AchievementDef): number {
  for (const c of conditionsOf(a)) if (c.metric.includes(":")) return c.threshold;
  return 0;
}

/** Should this achievement's name/description/rewards be hidden from the player right now? */
export function isRedacted(a: AchievementDef, unlocked: ReadonlySet<string>): boolean {
  return !!a.secret && !unlocked.has(a.id);
}

/** All pot-skin ids granted by a set of achievement ids (for gating the skin picker). Pass the COLLECTED set
 *  (App `clubCollected` = earned − `achClubHeld`), not the earned one: a skin is a Garden Club reward — see `standardGrantOf`. */
export function skinsFromAchievements(unlocked: Iterable<string>): Set<string> {
  const skins = new Set<string>();
  for (const id of unlocked) {
    const skin = BY_ID.get(id)?.reward?.potSkin;
    if (skin) skins.add(skin);
  }
  return skins;
}

/** Every cosmetic a set of achievements unlocks, as "kind:id" (sim/cosmetics.ts) — `unlocks` plus the older
 *  `potSkin` field, which is the same thing for one kind. Pass the COLLECTED set, as for skinsFromAchievements. */
export function cosmeticsFromAchievements(unlocked: Iterable<string>): Set<string> {
  const out = new Set<string>();
  for (const id of unlocked) {
    const r = BY_ID.get(id)?.reward;
    if (!r) continue;
    if (r.potSkin) out.add(`pot:${r.potSkin}`);
    for (const u of r.unlocks ?? []) out.add(u);
  }
  return out;
}

/** Achievement ids with the GRANTED ones (backer badges) first, everything else in its original order. A
 *  redeemed badge is appended to the end of the unlocked list, so a case that shows only its first few badges
 *  (the profile shows 8) would hide a Founder medal on any established player. Stable: unlock order survives. */
export function grantedFirst(ids: readonly string[]): string[] {
  const g = (id: string) => BY_ID.get(id)?.granted === true;
  return [...ids.filter(g), ...ids.filter((id) => !g(id))];
}

/**
 * BACKER-EXCLUSIVE cosmetics, as "kind:id": unlocked ONLY by a GRANTED achievement (a Kickstarter backer reward)
 * and by nothing a player can earn. A picker that shows locked rewards with a padlock (the Console's Skins tab)
 * must leave these out unless held — a padlock that no amount of play can open, shown to every non-backer
 * forever, reads as a goal they have failed (the same reason `visibleAchievements` hides the badge itself).
 * Derived from the live reward data on every call, so a skin later attached to a playable achievement stops
 * being exclusive by itself. Every Kickstarter cosmetic an admin can grant on its own (sim/backerCosmetics.ts) is
 * a backer reward too, whether or not a badge carries it (name styles are not skins and are left out).
 */
export function backerExclusiveCosmetics(): Set<string> {
  const granted = new Set<string>([...BACKER_COSMETIC_KEYS].filter((k) => !k.startsWith("name:"))), earnable = new Set<string>();
  for (const a of ACHIEVEMENTS) {
    if (a.enabled === false || !a.reward) continue;
    const keys = [...(a.reward.unlocks ?? []), ...(a.reward.potSkin ? [`pot:${a.reward.potSkin}`] : [])];
    for (const k of keys) (a.granted ? granted : earnable).add(k);
  }
  for (const k of earnable) granted.delete(k);
  return granted;
}

/** The ONE-TIME grant an achievement pays out (everything except the derived pot skin). Returns null when
 *  there's nothing to grant, so the caller can skip the whole apply path. */
export function grantOf(a: AchievementDef): Omit<AchievementReward, "potSkin" | "unlocks"> | null {
  const r = a.reward;
  if (!r) return null;
  const { potSkin: _skin, unlocks: _unlocks, ...grant } = r;
  const any = (grant.coins ?? 0) || (grant.petals ?? 0) || (grant.xp ?? 0)
    || (grant.fishXp ?? 0) || (grant.bugXp ?? 0) || Object.keys(grant.seeds ?? {}).length
    || Object.keys(grant.decor ?? {}).length || (grant.topMarkers ?? 0);
  return any ? grant : null;
}

/*
 * STANDARD vs GARDEN CLUB — the two halves every achievement reward splits into.
 *
 * STANDARD is the currency an achievement pays EVERY player the moment it unlocks: coins, petals and the three
 * kinds of XP. GARDEN CLUB is everything else — the THINGS: decor, trophies, seeds, Head Gardener's top
 * markers, and the cosmetic unlocks (pot / greenhouse / floor / shelf skins). Those are claimed only by a member
 * (sim/subscription.ts), under the same rule as the point track: A NON-MEMBER LOSES NOTHING. Earning an
 * achievement QUALIFIES its Garden Club half for good; the player's collected set (earned − the save's `achClubHeld`) is the receipt for
 * having collected it. A member collects on unlock; a player who joins later collects every held one at once.
 *
 * Once collected, always kept — a lapsed member keeps every skin and item they collected, because the skins
 * are derived from the COLLECTED set (not from the live subscription) and the items are already in the bag.
 */

/** Which reward fields are STANDARD (paid to everyone). Everything not listed here is Garden Club. */
const STANDARD_FIELDS = ["coins", "petals", "xp", "fishXp", "bugXp"] as const;

/** The standard half of a reward — the currency every player is paid on unlock — or null if it pays none. */
export function standardGrantOf(a: AchievementDef): Pick<AchievementReward, typeof STANDARD_FIELDS[number]> | null {
  const r = a.reward;
  if (!r) return null;
  const out: Pick<AchievementReward, typeof STANDARD_FIELDS[number]> = {};
  for (const k of STANDARD_FIELDS) if (r[k]) out[k] = r[k];
  return Object.keys(out).length ? out : null;
}

/** The ONE-TIME Garden Club half of a reward — seeds, decor/trophies, top markers — or null if it grants no
 *  object. (Its cosmetic unlocks are not in here: like `potSkin`, they are derived from the collected set.) */
export function clubGrantOf(a: AchievementDef): Pick<AchievementReward, "seeds" | "decor" | "topMarkers"> | null {
  const r = a.reward;
  if (!r) return null;
  const out: Pick<AchievementReward, "seeds" | "decor" | "topMarkers"> = {};
  // SEED IDS ARE CHECKED HERE, at the grant, the way `decorGrants` checks decor keys: `normaliseReward` keeps
  // unknown ids so the editor can flag them, but a seed of a species that does not exist would land in the bag,
  // show as the first flower's packet (`flower()` falls back) and plant an id no renderer knows.
  const seeds = Object.entries(r.seeds ?? {}).filter(([id]) => Object.prototype.hasOwnProperty.call(FLOWERS, id));
  if (seeds.length) out.seeds = Object.fromEntries(seeds) as Partial<Record<FlowerId, number>>;
  if (Object.keys(r.decor ?? {}).length) out.decor = r.decor;
  if (r.topMarkers) out.topMarkers = r.topMarkers;
  return Object.keys(out).length ? out : null;
}

/** Does this achievement carry ANY Garden Club reward — an object to collect or a cosmetic to unlock? */
export function hasClubReward(a: AchievementDef): boolean {
  const r = a.reward;
  return !!r && (!!clubGrantOf(a) || !!r.potSkin || !!r.unlocks?.length);
}

/** The earned achievements whose Garden Club half is still HELD — qualified, not yet collected. */
export function heldClubRewards(unlocked: Iterable<string>, collected: ReadonlySet<string>): string[] {
  const out: string[] = [];
  for (const id of unlocked) {
    const a = BY_ID.get(id);
    if (a && !collected.has(id) && hasClubReward(a)) out.push(id);
  }
  return out;
}

/**
 * A short player-facing summary of a reward, for the modal + the list row ("+250 coins, Porcelain pot").
 *
 * PETALS ARE PRINTED AT WHAT THIS PLAYER IS PAID, not at the authored base. Every petal grant in the game
 * runs through `grantedPetals`, so a Garden Club member's achievement pays 3×; before `subscriber` existed
 * here every achievement surface printed the base and a member was told "+50 petals" while 150 landed. This
 * is the same board-vs-payout fix the daily check-in board already carries — one funnel, `grantedPetals`,
 * and NOTHING in the UI multiplies by 3 on its own.
 *
 * It DEFAULTS FALSE — the base value — because the un-updated callers are the dev reward editor and any
 * catalogue-style listing, where the authored number is the right answer. A player-facing surface must pass
 * the resolved flag; App resolves `isSubscriber` once per render and hands it down.
 *
 * Only petals scale. Coins and XP have no multiplier, and inventing one here would quietly promise a payout
 * `grantOf` does not make.
 */
export function rewardSummary(
  r: AchievementReward | undefined,
  skinName: (id: string) => string = (x) => unlockName("pot", x),
  /** Decor id → display name. Defaults to the raw id so a caller that has no catalogue handy still reads. */
  decorName: (id: string) => string = (x) => x,
  /** Garden Club member? Scales the petals line to what is actually paid. */
  subscriber = false,
): string[] {
  if (!r) return [];
  const out: string[] = [];
  if (r.coins) out.push(`+${r.coins.toLocaleString()} coins`);
  if (r.petals) out.push(`+${grantedPetals(r.petals, subscriber).toLocaleString()} Petals`);
  if (r.xp) out.push(`+${r.xp.toLocaleString()} Gardening XP`);
  if (r.fishXp) out.push(`+${r.fishXp.toLocaleString()} Fishing XP`);
  if (r.bugXp) out.push(`+${r.bugXp.toLocaleString()} Bug Catching XP`);
  // DISPLAY NAMES ONLY — a seed is named by its plant, a per-species trophy ("goldStatue:plant:rose") by the
  // piece it is ("Gold Rose Statue"); the raw id must never reach the player
  for (const [id, n] of Object.entries(r.seeds ?? {})) if (n) out.push(`${n}× ${unlockName("plant", id)} seeds`);
  for (const [id, n] of Object.entries(r.decor ?? {})) {
    if (!n) continue;
    out.push(id.includes(":") ? unlockName("decor", id) : `${n}× ${decorName(id)}`);
  }
  if (r.topMarkers) out.push(`Botanical Markers for your ${r.topMarkers} most-harvested plants`);
  if (r.potSkin) out.push(`${skinName(r.potSkin)} pot skin`);
  for (const u of r.unlocks ?? []) {
    const [kind, id] = [u.slice(0, u.indexOf(":")), u.slice(u.indexOf(":") + 1)];
    if (kind === "pot") out.push(`${unlockName("pot", id)} pot skin`);
    else if (kind === "decor") out.push(`${decorName(id)} in the Market`);
    else out.push(`${unlockName(kind, id)} ${kind === "building" ? "greenhouse" : kind === "floor" ? "floor" : kind === "frame" ? "avatar border" : kind === "fence" ? "fence" : "racks"}`);
  }
  return out;
}
